/**
 * ============================================
 *  CART UPSELL — "You May Also Like" carousel
 *  SH Updated: June 2026 - P1 module 2 (paid upsell carousel in cart drawer)
 * ============================================
 *
 * Renders a curated, horizontally-scrollable upsell carousel below the cart
 * line items. Mirrors the GWP manager lifecycle: a persistent #CartUpsellContainer
 * (placed outside .Cart__ItemList so the theme's surgical re-render preserves it),
 * repainted on every `gwp:refresh` the CartSection fires after a cart update.
 *
 * Products are curated in Theme Settings (Cart Upsell → product list). Adding from
 * a card posts to /cart/add.js then dispatches `product:added`, which the theme's
 * CartSection handles (re-render in place + drawer open) — no page reload.
 */
class CartUpsellManager {
  constructor() {
    this.config = window.cartUpsellConfig || { enabled: false, title: '', products: [] };
    this.flickityInstance = null;
    this._delegated = false;
  }

  /**
   * Fetch the cart, decide which curated products to show, render + wire them.
   */
  async update() {
    const container = document.getElementById('CartUpsellContainer');
    if (!container) return;

    if (!this.config.enabled || !this.config.products || this.config.products.length === 0) {
      this.clear(container);
      return;
    }

    let cart;
    try {
      cart = await fetch('/cart.js').then(r => r.json());
    } catch (e) {
      return;
    }

    // Empty cart → no upsell.
    if (!cart.items || cart.items.length === 0) {
      this.clear(container);
      return;
    }

    // Don't upsell what's already in the cart (match on product id).
    const productIdsInCart = new Set(cart.items.map(it => it.product_id));

    const candidates = this.config.products.filter(p =>
      p.available && p.variantId && !productIdsInCart.has(p.id)
    );

    if (candidates.length === 0) {
      this.clear(container);
      return;
    }

    this.render(container, candidates);
  }

  render(container, products) {
    // Tear down any prior carousel before repainting.
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }

    const cardsHtml = products.map(p => `
      <div class="Carousel__Cell cart-upsell-card" data-product-id="${p.id}">
        <a href="${p.url}" class="cart-upsell-card__image" tabindex="-1" aria-hidden="true">
          <img src="${p.image}" alt="${this.escape(p.title)}" loading="lazy">
        </a>
        <div class="cart-upsell-card__info">
          <div class="cart-upsell-card__title">${this.escape(p.title)}</div>
          ${p.description ? `<div class="cart-upsell-card__desc">${this.escape(p.description)}</div>` : ''}
          <button type="button" class="cart-upsell-card__add Button Button--primary"
                  data-variant="${p.variantId}" data-product-id="${p.id}">
            <span class="cart-upsell-card__add-label">Add to Bag</span>
            <span class="cart-upsell-card__add-dot"></span>
            <span class="cart-upsell-card__add-price">${this.formatMoney(p.price)}</span>
          </button>
        </div>
      </div>
    `).join('');

    container.innerHTML = `
      <div class="cart-upsell">
        <div class="cart-upsell__heading">${this.escape(this.config.title || 'You May Also Like')}</div>
        <div class="cart-upsell-slider Carousel">${cardsHtml}</div>
      </div>
    `;
    container.style.display = 'block';

    this.initFlickity();
    this.attachListeners();
  }

  clear(container) {
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }
    container.innerHTML = '';
    container.style.display = 'none';
  }

  initFlickity() {
    const slider = document.querySelector('.cart-upsell-slider');
    if (!slider) return;
    requestAnimationFrame(() => {
      if (typeof Flickity === 'undefined') {
        console.error('CartUpsell: Flickity not loaded');
        return;
      }
      try {
        this.flickityInstance = new Flickity(slider, {
          prevNextButtons: true,
          pageDots: false,
          wrapAround: false,
          contain: true,
          cellAlign: 'left',
          watchCSS: false,
          dragThreshold: 8,
          groupCells: false,
          arrowShape: { x0: 20, x1: 60, y1: 40, x2: 60, y2: 35, x3: 25 }
        });
      } catch (error) {
        console.error('CartUpsell Flickity Error:', error);
      }
    });
  }

  /**
   * Delegated click handler — attached once to the container, so it survives
   * every repaint without re-binding.
   */
  attachListeners() {
    if (this._delegated) return;
    const container = document.getElementById('CartUpsellContainer');
    if (!container) return;
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-upsell-card__add');
      if (!btn) return;
      // Ignore clicks that were really a carousel drag.
      if (this.flickityInstance && this.flickityInstance.isDragging) return;
      this.addToCart(btn);
    });
    this._delegated = true;
  }

  async addToCart(button) {
    const variantId = button.dataset.variant;
    if (!variantId || button.disabled) return;

    const label = button.querySelector('.cart-upsell-card__add-label');
    const original = label ? label.textContent : '';
    button.disabled = true;
    if (label) label.textContent = 'Adding…';
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      if (label) label.textContent = 'Added!';

      // Let the theme re-render the cart in place + open the drawer.
      // CartSection re-render fires `gwp:refresh`, which repaints this carousel.
      document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
    } catch (error) {
      console.error('CartUpsell add error:', error);
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button.disabled = false;
      if (label) label.textContent = original || 'Add to Bag';
    }
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
  }

  escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}

(function initCartUpsell() {
  const boot = () => {
    window.cartUpsellManager = new CartUpsellManager();
    window.cartUpsellManager.update();
    // CartSection dispatches `gwp:refresh` after every drawer re-render.
    document.addEventListener('gwp:refresh', () => {
      if (window.cartUpsellManager) window.cartUpsellManager.update();
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
